import type { protocol } from "@codesandbox/pitcher-client";
import { match } from "@codesandbox/states";
import type { Terminal as XTerminal } from "@xterm/xterm";
import { useEnvironmentInterface } from "environment-interface";
import { useShell } from "features/shell/Feature";
import { useLegacyPitcher } from "features/utils/usePitcher";
import { useSession } from "hooks/api/useSession";
import debounce from "lodash/debounce";
import type { Tokens } from "prism-react";
import { Box, useThemeTokens, useTheme } from "prism-react";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { DevtoolLoading } from "../common/DevtoolLoading";

import { ShellError } from "./ShellError";
import { computeShellSize } from "./utils";

type ShellVariant = "command" | "terminal";
interface ShellProps {
  variant: ShellVariant;
  readonly?: boolean;
  onOpenError?: () => void;
  // Used for exceptional cases, like when the shell is rendered inside another panel (setup tasks)
  overrideBackgroundColor?: keyof Tokens["colors"];
}
export interface ShellRef {
  focus?: () => void;
  clear?: () => void;
}
/**
 * This is an abritraty low value, which should be close to zero,
 * but slightly higher to ensure at least something is still rendered
 * if the shell's parent has a very small height.
 *
 * In practice this is used to prevent the shell re-drawing nothing when
 * a shell's parent component is collapsed/minimized.
 */
const REDRAW_SHELL_HEIGHT_THRESHOLD = 10;

export const Shell = forwardRef<ShellRef, ShellProps>(
  (
    {
      readonly = false,
      onOpenError,
      variant = "command",
      overrideBackgroundColor,
    },
    ref,
  ) => {
    const {
      terminal: { createTerminal },
    } = useEnvironmentInterface();
    const [sessionState] = useSession();
    const theme = useThemeTokens();
    const { theme: currentThemeName } = useTheme();

    const pitcher = useLegacyPitcher();
    const shellDomNodeRef = useRef<HTMLDivElement>(null);
    const shellWrapperDomNodeRef = useRef<HTMLDivElement>(null);
    const sizeRef = useRef<protocol.shell.ShellSize>({ rows: 1, cols: 1 });
    const xtermRef = useRef<XTerminal>();

    const [shell, send] = useShell();

    const isInputDisabled = readonly || variant === "command";

    const vsCodeThemeActiveTab = getComputedStyle(
      document.body,
    ).getPropertyValue("--vscode-tab-activeBackground");

    // transparent background to avoid terminal flicker while the theme is not yet fully loaded in the editor
    const fallbackBgColor = "#00000000";

    const activeTabFromTheme = overrideBackgroundColor
      ? theme.colors[overrideBackgroundColor]
      : vsCodeThemeActiveTab || fallbackBgColor;

    const terminalTheme = {
      background: activeTabFromTheme,
      foreground: theme.colors["neutral-fg-high"],
      cursor: theme.colors["neutral-fg-high"],
      cursorAccent: theme.colors["neutral-fg-high"],
      brightGreen: "#85CEA8",
      brightMagenta: theme.colors["accent-secondary-base"],
      magenta: "#7660B9",
      green: "#319382",
      yellow: theme.colors["informative-warning-base"],
      brightYellow: theme.colors["informative-warning-base"],
      red: theme.colors["informative-error-base"],
      // Not red and cyan but style the colors used in the prompt header.
      brightRed: "#808080",
      brightCyan: theme.colors["accent-primary-base"],
    };

    useEffect(() => {
      if (
        shell.state !== "INITIALIZING" ||
        shellWrapperDomNodeRef.current === null
      ) {
        return;
      }

      const node = shellWrapperDomNodeRef.current;
      const { width, height } = node.getBoundingClientRect();
      // Approximate the size, but soon after we'll send the real size
      const { cols, rows } = computeShellSize(width, height);

      if (shell.shellId) {
        pitcher.shell.openShell(shell.shellId, { rows, cols });
      }
    }, [shell.state]);

    useEffect(() => {
      if (shell.state === "ERROR" && onOpenError) {
        onOpenError();
      }
    }, [shell.state]);

    // Moved here to avoid passing a ref through the feature
    useEffect(() => {
      if (shell.state !== "READY" || shellDomNodeRef.current === null) {
        return;
      }

      const shellNode = shellDomNodeRef.current;

      const disposables: {
        resizeObserver?: ResizeObserver;
        xterm?: XTerminal;
      } = {};

      createTerminal(
        {
          onPath(path, isAbsolute, position) {
            send({
              type: "PATH_CLICK",
              path,
              isAbsolute,
              position,
            });
          },
          onUrl(url) {
            send({
              type: "URL_CLICK",
              url,
            });
          },
        },
        {
          cursorBlink: isInputDisabled ? false : true,
          cursorStyle: isInputDisabled ? "underline" : "bar",
          fontFamily:
            "FiraCode, SFMono-Regular, Consolas, 'Liberation Mono', Menlo, Courier, monospace",
          theme: terminalTheme,
          fontSize: 13,
          lineHeight: 1.3,
        },
      ).then((terminal) => {
        const { term: xterm, fit } = terminal;

        const resize = debounce(() => {
          // The sidekick is hidden by setting its width
          // to 0
          if (!shellNode.clientWidth) {
            return;
          }

          fit();

          const { cols, rows } = xterm;

          sizeRef.current = { cols, rows };

          const isShellOwner =
            sessionState.state === "AUTHENTICATED" &&
            sessionState.user.username.toLowerCase() === shell.ownerUsername;
          if (isShellOwner) {
            // If we're the owner of the shell, automatically resize
            pitcher.shell.resizeShell(shell.id, sizeRef.current);
          }
        }, 50);

        try {
          const resizeObserver = new ResizeObserver(([el]) => {
            // Do not re-fit the shell when the height of the shell
            // is below this threshold.
            if (el!.contentRect.height < REDRAW_SHELL_HEIGHT_THRESHOLD) {
              return;
            }

            resize();
          });

          disposables.resizeObserver = resizeObserver;

          resizeObserver.observe(shellNode);
        } catch (error) {
          // Ignore ResizeObserver exceptions:
          // `ResizeObserver loop completed with undelivered notifications.`

          // eslint-disable-next-line no-console
          console.warn(error);
        }

        xterm.open(shellNode);

        const lines = shell.buffer;

        if (lines.length > 1) {
          // When there are multiple lines, add all but the last one
          // Adding a line break at the end of the line
          lines.slice(-1);
          lines.forEach((row) => {
            xterm.writeln(row);
          });
        }

        const lastLine = lines[lines.length - 1]!;
        // Print the final line if not empty string, without a line break
        if (lastLine !== "") {
          xterm.write(lastLine);
        }

        pitcher.shell.listenShell(shell.id, (out) => {
          // if (out.includes("\r\u001b[K")) {
          xterm.write(out);
        });

        xterm.onData((input) =>
          pitcher.shell.writeToShell(shell.id, input, sizeRef.current),
        );

        if (!isInputDisabled) {
          xterm.focus();
        }

        xtermRef.current = xterm;
      });

      return () => {
        disposables.resizeObserver?.disconnect();
        disposables.xterm?.dispose();
        pitcher.shell.closeShell(shell.id);
      };
    }, [shell.state]);

    useEffect(() => {
      if (xtermRef.current) {
        xtermRef.current.options.theme = terminalTheme;
      }
    }, [currentThemeName.name, activeTabFromTheme]);

    useImperativeHandle(ref, () => ({
      focus: () => {
        xtermRef.current?.focus();
      },
      clear: () => {
        xtermRef.current?.clear();
      },
    }));

    return (
      <Box
        ref={shellWrapperDomNodeRef}
        css={{
          height: "100%",
          position: "relative",
        }}
      >
        {match(shell, {
          CONNECTING: () => <DevtoolLoading message="Connecting" />,
          INITIALIZING: () => <DevtoolLoading message="Connecting" />,
          ERROR: ({ error }) => <ShellError message={error} />,
          READY: () => {
            return (
              <Box
                css={{
                  height: "100%",
                  opacity: readonly ? 0.8 : 1,
                }}
              >
                <div ref={shellDomNodeRef} style={{ height: "100%" }} />
              </Box>
            );
          },
        })}
      </Box>
    );
  },
);
